import React, { useState, useEffect } from 'react';
import CalendarView from './CalendarView';
import { ProjectedTaskDetail, TemplateJobProjectionResponse } from '../../../../interfaces/adminUser/adminRecurringTaskStats/AdminRecurringTaskJobStats';
import { getMonthlyProjections, getRetroactiveMissedTasks } from '../../../../services/adminStatsServices/AdminRecurringTaskJobStatsService';
import { useAuth } from '../../../../firebase/AuthProvider';
import DetailList, { MissedTasksView } from './DetailList';

// Simple types for dropdowns (Mocking the org list for this example)
interface OrganizationOption {
    id: number;
    name: string;
}

const MOCK_ORGS: OrganizationOption[] = [
    { id: 1, name: "SAMPLE SCA AGENCY" },
    { id: 100, name: "Easter Seals" },
];

export const AdminTemplateDashboard: React.FC = () => {
    const { currentUser } = useAuth();

    // --- State ---
    const [selectedOrg, setSelectedOrg] = useState<number>(MOCK_ORGS[0].id);
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1); // 1-12
    
    const [projectionData, setProjectionData] = useState<TemplateJobProjectionResponse | null>(null);
    const [missedTasks, setMissedTasks] = useState<ProjectedTaskDetail[]>([]);
    const [viewMode, setViewMode] = useState<'calendar' | 'missed'>('calendar');
    
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedDayDetails, setSelectedDayDetails] = useState<ProjectedTaskDetail[] | null>(null);
    const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);

    // --- Actions ---
    const fetchData = async () => {
        setLoading(true);
        // In a real app, retrieve token from your auth context
        if (!currentUser) return;
        const authToken = await currentUser.getIdToken();
        
        if (viewMode === 'calendar') {
            const data = await getMonthlyProjections(authToken, selectedOrg, year, month);
            setProjectionData(data);
        } else {
            const data = await getRetroactiveMissedTasks(authToken, selectedOrg, 90);
            setMissedTasks(data || []);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchData();
    }, [selectedOrg, year, month, viewMode, currentUser]);

    // --- Helpers ---
    const handleDayClick = (day: string) => {
        if (projectionData && projectionData.dailyProjections[day]) {
            setSelectedDayDetails(projectionData.dailyProjections[day]);
            setIsPanelOpen(true);
        } else {
            setSelectedDayDetails([]);
            // Optional: Open panel even if empty to show "No tasks"
        }
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen font-sans">
            {/* Header / Controls */}
            <div className="bg-white p-4 rounded-lg shadow mb-6 flex flex-wrap gap-4 items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Recurring Job Auditor</h1>
                    <p className="text-sm text-gray-500">Visualize automated task creation logic</p>
                </div>
                
                <div className="flex gap-3">
                    <select 
                        className="border rounded px-3 py-2"
                        value={selectedOrg} 
                        onChange={(e) => setSelectedOrg(Number(e.target.value))}
                    >
                        {MOCK_ORGS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>

                    <select 
                        className="border rounded px-3 py-2"
                        value={year} 
                        onChange={(e) => setYear(Number(e.target.value))}
                    >
                        {[2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                    </select>

                    <select 
                        className="border rounded px-3 py-2"
                        value={month} 
                        onChange={(e) => setMonth(Number(e.target.value))}
                    >
                        {Array.from({length: 12}, (_, i) => i + 1).map(m => (
                            <option key={m} value={m}>{new Date(0, m - 1).toLocaleString('default', { month: 'long' })}</option>
                        ))}
                    </select>
                </div>

                <div className="flex border rounded overflow-hidden">
                    <button 
                        className={`px-4 py-2 ${viewMode === 'calendar' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700'}`}
                        onClick={() => setViewMode('calendar')}
                    >
                        Calendar Projection
                    </button>
                    <button 
                        className={`px-4 py-2 ${viewMode === 'missed' ? 'bg-red-600 text-white' : 'bg-white text-gray-700'}`}
                        onClick={() => setViewMode('missed')}
                    >
                        Retroactive Missed
                    </button>
                </div>
            </div>

            {/* Main Content Area */}
            {loading ? (
                <div className="text-center py-20 text-gray-500">Running logic simulation...</div>
            ) : (
                <>
                    {viewMode === 'calendar' && projectionData && (
                        <CalendarView 
                            year={year} 
                            month={month} 
                            data={projectionData} 
                            onDayClick={handleDayClick} 
                        />
                    )}

                    {viewMode === 'missed' && (
                        <MissedTasksView tasks={missedTasks} />
                    )}
                </>
            )}

            {/* Side Panel Overlay */}
            {isPanelOpen && (
                <div className="fixed inset-0 bg-gray-500 bg-opacity-30 z-50 flex justify-end">
                    <div className="bg-white w-full max-w-2xl h-full shadow-xl overflow-y-auto p-6 animate-slide-in">
                        <div className="flex justify-between items-center mb-6 border-b pb-4">
                            <h2 className="text-xl font-bold">Projected Details</h2>
                            <button onClick={() => setIsPanelOpen(false)} className="text-gray-500 hover:text-black">
                                ✕ Close
                            </button>
                        </div>
                        <DetailList tasks={selectedDayDetails || []} />
                    </div>
                </div>
            )}
        </div>
    );
};